import { makeElement, addClass, toggleClass } from '../../common/Utils';
import { clearTimer, isFunction } from '../util/util';
import { NCUI_MODAL_PREFIX, modalClass, popupClass } from '../constants/class';
import { handleState, langArr } from '../constants/state';
import { makeModalContent } from '../util/renderModal';
import {
    ModalOption,
    ModalPromiseResolve,
    langType,
    ModalL10n,
} from '../../interface/IModal';
import { modalL10n } from '../l10n';
import { initialOptions } from '../constants/options';

export default abstract class AbstractNotification {
    private l10n!: ModalL10n;
    protected $backdrop!: HTMLDivElement;
    protected $modalContent!: HTMLDivElement;
    protected $confirmButton: HTMLButtonElement | null = null;
    protected $cancelButton: HTMLButtonElement | null = null;
    protected $closeButton: HTMLButtonElement | null = null;
    protected modalTimer: number | null = null;
    protected isOpen: boolean = false;
    protected options: ModalOption = initialOptions;

    // 모달 열기
    protected open(options: ModalOption): void {
        this.initModal();
        this.optionsSetting(options);

        this.isOpen = true;
        this.$backdrop.innerHTML = makeModalContent(this.options);
        this.setModalDom();

        this.addClass();
        this.addClassForAnimation();

        this.addEvents();
        this.appendModalInBody();

        this.addWillOpen();
    }
    // 모달 닫기
    protected close(): void {
        clearTimer(this.modalTimer);
        if (!this.$backdrop.classList.contains(modalClass.BACKDROP_SHOW))
            return;
        this.isOpen = false;
        this.removeClassForAnimation();
    }
    // modal 초기화
    protected initModal(): void {
        if (this.$backdrop) this.$backdrop.remove();
        this.options = initialOptions;
        this.makeBackdropElement();
        clearTimer(this.modalTimer);

        this.setLang();
        this.setL10nText();
    }
    private setLang(): void {
        const lang =
            (document
                .querySelector('html')!
                .getAttribute('lang') as langType) || "";
        this.l10n = modalL10n[langArr.indexOf(lang) !== -1 ? lang : 'en'];
    }
    private setL10nText(): void {
        this.options = {
            ...this.options,
            cancelButtonText: this.l10n.actionButton.cancel,
            confirmButtonText: this.l10n.actionButton.confirm,
        };
    }
    private makeBackdropElement(): void {
        this.$backdrop = makeElement(
            `<div class="${modalClass.BACKDROP}"></div>`
        ) as HTMLDivElement;
    }
    private optionsSetting(options: ModalOption): void {
        this.options = { ...this.options, ...options };
    }
    private setModalDom(): void {
        this.$modalContent = this.$backdrop.querySelector<HTMLDivElement>(
            `.${modalClass.CONTAINER}`
        )!;
        this.$cancelButton = this.$backdrop.querySelector(
            `.${modalClass.BUTTON_CANCEL}`
        );
        this.$confirmButton = this.$backdrop.querySelector(
            `.${modalClass.BUTTON_CONFIRM}`
        );
        this.$closeButton = this.$backdrop.querySelector(
            `.${modalClass.BUTTON_CLOSE}`
        );
    }
    private addEvents(): void {
        this.$cancelButton?.addEventListener('click', () => this.close());
        this.$confirmButton?.addEventListener('click', () => this.close());
        this.$closeButton?.addEventListener('click', () => this.close());
        this.$backdrop.addEventListener('animationend', () => {
            if (!this.isOpen) this.initModal();
        });
    }
    protected addEventsByOptions(resolve: ModalPromiseResolve): void {
        this.addConfirmBtnListener(resolve);
        this.addEnterKeyListener(resolve);
        this.addCancelBtnListener(resolve);
        this.addCloseBtnListener(resolve);
    }
    private addEnterKeyListener(resolve: ModalPromiseResolve): void {
        if (this.options.allowEnterKey) {
            this.$cancelButton?.addEventListener('keydown', (e) => {
                if (e.key === 'Enter') {
                    resolve({
                        value: handleState.CANCEL,
                        isConfirmed: false,
                        isCancel: true,
                    });
                }
            });
            this.$confirmButton?.addEventListener('keydown', (e) => {
                if (e.key === 'Enter') {
                    resolve({
                        value: handleState.CONFIRM,
                        isConfirmed: true,
                        isCancel: false,
                    });
                }
            });
        }
    }
    private addConfirmBtnListener(resolve: ModalPromiseResolve): void {
        this.$confirmButton?.addEventListener('click', (e) => {
            e.stopPropagation();
            if (this.options.preConfirm && isFunction(this.options.preConfirm))
                this.options.preConfirm();
            resolve({
                value: handleState.CONFIRM,
                isConfirmed: true,
                isCancel: false,
            });
        });
    }
    private addCancelBtnListener(resolve: ModalPromiseResolve): void {
        this.$cancelButton?.addEventListener('click', (e) => {
            e.stopPropagation();
            if (this.options.willClose && isFunction(this.options.willClose))
                this.options.willClose();
            resolve({
                value: handleState.CANCEL,
                isConfirmed: false,
                isCancel: true,
            });
        });
    }
    private addCloseBtnListener(resolve: ModalPromiseResolve): void {
        this.$closeButton?.addEventListener('click', (e) => {
            e.stopPropagation();
            resolve({
                value: handleState.CLOSE,
                isConfirmed: false,
                isCancel: false,
            });
        });
    }
    private addWillOpen() {
        if (this.options.willOpen && isFunction(this.options.willOpen))
            this.options.willOpen();
    }
    private appendModalInBody(): void {
        document.body.appendChild(this.$backdrop);
    }
    protected focusOnButton(): void {
        if (!this.options.showConfirmButton && !this.options.showCancelButton)
            return;

        this.options.showCancelButton
            ? this.$cancelButton?.focus()
            : this.$confirmButton?.focus();
    }
    protected setTimerOption(resolve: ModalPromiseResolve): void {
        if (this.options.timer && this.options.timer > 0) {
            this.modalTimer = window.setTimeout(() => {
                this.close();
                resolve({
                    value: handleState.TIMER,
                    isCancel: true,
                    isConfirmed: false,
                });
            }, this.options.timer);
        }
    }
    private addClass(): void {
        addClass(
            this.$backdrop,
            `${NCUI_MODAL_PREFIX}${this.options.position}`
        );
    }
    private addClassForAnimation(): void {
        addClass(this.$backdrop, modalClass.BACKDROP_SHOW);
    }
    protected removeClassForAnimation(): void {
        if (!!this.options.hideClass) {
            toggleClass(this.$backdrop, this.options.hideClass);
        } else {
            toggleClass(this.$backdrop, modalClass.BACKDROP_SHOW);
            toggleClass(this.$backdrop, modalClass.BACKDROP_HIDE);
        }
        toggleClass(this.$modalContent, popupClass.POPUP_CONTAINER_SHOW);
        toggleClass(this.$modalContent, popupClass.POPUP_CONTAINER_HIDE);
    }
}
